/**
 * Form helpers for create/edit program views.
 */
import type {
  CreateProgramDto,
  Program,
  ProgramFormValue,
  UpdateProgramDto,
} from '@/types/program.types';
import { PROGRAM_TYPE_CONFIG } from './program-configs';
import { getErrorMessage } from '@/utils/errors-utils';

/** Normalises the raw availability shape from the API. */
export function normalizeProgramAvailability(raw: unknown): {
  weekly: Array<{ day: number; start: string; end: string }>;
} {
  if (!raw || typeof raw !== 'object') return { weekly: [] };
  const obj = raw as Record<string, unknown>;
  if (obj.weekly) return raw as { weekly: Array<{ day: number; start: string; end: string }> };
  if (Array.isArray(obj.slots)) {
    return {
      weekly: (obj.slots as Array<Record<string, unknown>>).map((s) => ({
        day: (typeof s.dayOfWeek === 'number' ? s.dayOfWeek : 0) + 1,
        start: (s.startTime ?? s.start ?? '') as string,
        end: (s.endTime ?? s.end ?? '') as string,
      })),
    };
  }
  return { weekly: [] };
}

/** Empty create form; dates and type stay unset until the user fills them. */
export function emptyCreateProgramForm(): CreateProgramDto {
  return {
    establishmentId: '',
    schoolYearId: '',
    label: '',
    code: '',
    name: '',
    description: '',
    startDate: undefined,
    endDate: undefined,
    status: 'draft',
    type: null,
    availability: null,
    coursePathType: null,
  };
}

/** Patch one field (availability included). */
export function applyProgramFormField<T extends CreateProgramDto | UpdateProgramDto>(
  form: T,
  field: keyof T,
  value: ProgramFormValue,
): T {
  return { ...form, [field]: value };
}

/** Type {@link Select} options filtered by app mode (`in_person` hides remote, and vice versa). */
export function mapProgramTypeOptions(
  mode: string | undefined,
): Array<{ value: string; label: string }> {
  return Object.entries(PROGRAM_TYPE_CONFIG)
    .filter(([value]) => {
      if (mode === 'in_person') return value !== 'remote';
      if (mode === 'remote') return value !== 'in_person';
      return true;
    })
    .map(([value, config]) => ({ value, label: config.label }));
}

/** {@link DatePicker} value as `YYYY-MM-DD` (empty when unset). */
function toProgramDateInput(date: string | Date | null | undefined): string {
  if (!date) return '';
  if (typeof date === 'string') return date;
  return new Date(date).toISOString().split('T')[0];
}

/** Hydrate the edit form from GET program. */
export function programToFormData(program: Program): UpdateProgramDto {
  return {
    code: program.code,
    name: program.name,
    description: program.description || '',
    startDate: toProgramDateInput(program.startDate),
    endDate: toProgramDateInput(program.endDate),
    status: program.status,
    type: program.type ?? null,
    availability: normalizeProgramAvailability(program.availability),
    coursePathType: program.coursePathType ?? null,
  };
}

/** Nest create DTO — drop form-only ids that are empty strings (invalid UUID → 500). */
export function buildProgramSubmitDto<T extends CreateProgramDto | UpdateProgramDto>(form: T): T {
  return {
    name: form.name,
    code: form.code,
    description: form.description,
    startDate: form.startDate ? form.startDate : null,
    endDate: form.endDate ? form.endDate : null,
    status: form.status,
    type: form.type || null,
    coursePathType: form.coursePathType || null,
    rules: form.rules,
  } as T;
}

/** Central error copy from {@link getErrorMessage}. */
export function getProgramApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
